import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Flex,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Icon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useColorModeValue,
  Skeleton,
  Text,
  Badge,
  Card,
  CardBody,
  VStack,
  HStack,
  Tag,
  TagLabel,
  TagLeftIcon,
  Tooltip,
  Wrap,
  WrapItem,
  Avatar,
  Center,
  Select,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { FaCity, FaFilter, FaSync } from 'react-icons/fa';
import { GiCastle, GiVillage } from 'react-icons/gi';
import { MdAdminPanelSettings } from 'react-icons/md';
import { useRegionData } from '../../../../contexts/RegionDataContext';

const SajjaSection = ({ onCountUpdate }) => {
  const [searchInput, setSearchInput] = useState('');
  const [filterTahsil, setFilterTahsil] = useState('');

  const {
    subdivisions,
    tahsils,
    isLoading,
    searchSubdivisions,
    refreshSubdivisions,
    counts,
  } = useRegionData();

  const rowHoverBg = useColorModeValue('blue.50', 'gray.700');
  const headingColor = useColorModeValue('blue.800', 'blue.200');
  const inputTextColor = useColorModeValue('gray.800', 'white');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const cardBg = useColorModeValue('white', 'gray.800');
  const gradientStart = useColorModeValue('blue.400', 'blue.600');
  const gradientEnd = useColorModeValue('purple.500', 'purple.600');
  const accentColor = useColorModeValue('blue.500', 'blue.300');
  const subtitleColor = useColorModeValue('gray.600', 'gray.400');
  const tableHeaderBg = useColorModeValue('blue.600', 'blue.700');

  const filteredSubdivisions = useMemo(() => {
    return subdivisions.filter(
      (s) =>
        s.name?.toLowerCase().includes(searchInput.toLowerCase()) &&
        (filterTahsil ? s.tahsil?._id === filterTahsil : true),
    );
  }, [subdivisions, searchInput, filterTahsil]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    if (value) {
      searchSubdivisions(value);
    }
  };

  const handleTahsilChange = (e) => {
    const value = e.target.value;
    setFilterTahsil(value);
  };

  const handleRefresh = () => {
    refreshSubdivisions();
    setSearchInput('');
    setFilterTahsil('');
  };

  useEffect(() => {
    if (onCountUpdate && counts.sajja > 0) {
      onCountUpdate(counts.sajja);
    }
  }, [counts.sajja, onCountUpdate]);

  const stats = useMemo(() => {
    const total = subdivisions.length;
    const withTahsil = subdivisions.filter((s) => s.tahsil?.name).length;
    const uniqueDistricts = [
      ...new Set(subdivisions.map((s) => s.district?._id)),
    ].filter((id) => id).length;

    return {
      total,
      uniqueDistricts,
      tahsilCoverage: total > 0 ? Math.round((withTahsil / total) * 100) : 0,
    };
  }, [subdivisions]);

  return (
    <Box position="relative">
      <Card
        mb={4}
        borderRadius="lg"
        bg={cardBg}
        border="1px solid"
        borderColor={borderColor}
        overflow="hidden"
      >
        {/* COLOR STRIP - KEEP THIS */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          height="6px"
          bgGradient={`linear(to-r, ${gradientStart}, ${gradientEnd})`}
        />

        <CardBody p={4}>
          <Flex
            justify="space-between"
            align="center"
            mb={4}
            position="relative"
            zIndex={1}
            pt={2}
          >
            <Flex align="center" gap={4}>
              <Box
                p={3}
                borderRadius="xl"
                bg="blue.100"
                _dark={{ bg: 'blue.900' }}
                position="relative"
                overflow="hidden"
              >
                <Icon as={GiCastle} boxSize={6} color="blue.600" />
              </Box>
              <VStack align="flex-start" spacing={1}>
                <Heading size="md" color={headingColor} fontWeight="bold">
                  Sajja
                </Heading>
                <Text fontSize="sm" color={subtitleColor}>
                  {stats.total} administrative sajja across{' '}
                </Text>
              </VStack>
            </Flex>
          </Flex>

          <Box pb={2}>
            <Flex gap={4} wrap="wrap" align="center" justify="space-between">
              <InputGroup size="md" flex="1" minW="300px">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color={accentColor} />
                </InputLeftElement>
                <Input
                  placeholder="Search by Sajja name..."
                  value={searchInput}
                  onChange={handleSearchChange}
                  color={inputTextColor}
                  borderRadius="lg"
                  border="2px solid"
                  borderColor={borderColor}
                  _focus={{
                    borderColor: accentColor,
                    boxShadow: `0 0 0 3px ${accentColor}33`,
                  }}
                  _hover={{
                    borderColor: accentColor,
                  }}
                  fontSize="sm"
                />
              </InputGroup>

              <Flex gap={3} align="center">
                <Select
                  placeholder="Filter by Tahsil"
                  value={filterTahsil}
                  onChange={handleTahsilChange}
                  size="md"
                  borderRadius="lg"
                  minW="250px"
                  border="2px solid"
                  borderColor={borderColor}
                  _focus={{
                    borderColor: accentColor,
                    boxShadow: `0 0 0 1px ${accentColor}`,
                  }}
                >
                  <option value="">All Tahsils</option>
                  {tahsils.map((tahsil) => (
                    <option key={tahsil._id} value={tahsil._id}>
                      {tahsil.name}
                    </option>
                  ))}
                </Select>

                <Tooltip label="Refresh data">
                  <Flex
                    as="button"
                    p={2}
                    borderRadius="full"
                    bg={`${accentColor}10`}
                    onClick={handleRefresh}
                    cursor="pointer"
                    align="center"
                    justify="center"
                    _hover={{ bg: `${accentColor}20` }}
                    transition="all 0.2s"
                  >
                    <Icon as={FaSync} color={accentColor} boxSize={4} />
                  </Flex>
                </Tooltip>

                <Badge
                  colorScheme="orange"
                  variant="subtle"
                  px={3}
                  py={1}
                  borderRadius="full"
                >
                  {filteredSubdivisions.length} results
                </Badge>
              </Flex>
            </Flex>

            {(searchInput || filterTahsil) && (
              <Flex gap={2} mt={4} align="center">
                <Icon as={FaFilter} color={subtitleColor} boxSize={3} />
                <Text fontSize="xs" color={subtitleColor} mr={2}>
                  Active filters:
                </Text>
                <Wrap>
                  {searchInput && (
                    <WrapItem>
                      <Tag size="sm" colorScheme="blue" borderRadius="full">
                        <TagLeftIcon as={SearchIcon} />
                        <TagLabel>{searchInput}</TagLabel>
                      </Tag>
                    </WrapItem>
                  )}
                  {filterTahsil && (
                    <WrapItem>
                      <Tag size="sm" colorScheme="green" borderRadius="full">
                        <TagLeftIcon as={FaCity} />
                        <TagLabel>
                          {tahsils.find((t) => t._id === filterTahsil)?.name ||
                            'Tahsil'}
                        </TagLabel>
                      </Tag>
                    </WrapItem>
                  )}
                </Wrap>
              </Flex>
            )}
          </Box>

          <Box position="relative" zIndex={1}>
            {isLoading.subdivisions ? (
              <VStack spacing={4} align="stretch">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} height="60px" borderRadius="lg" />
                ))}
              </VStack>
            ) : filteredSubdivisions.length === 0 ? (
              <Card
                borderRadius="xl"
                border="2px dashed"
                borderColor={borderColor}
                bg={cardBg}
              >
                <CardBody>
                  <VStack spacing={4} p={8} textAlign="center">
                    <Box
                      p={6}
                      borderRadius="full"
                      bg={`${accentColor}15`}
                      border={`2px solid ${accentColor}30`}
                    >
                      <Icon as={GiCastle} boxSize={12} color={accentColor} />
                    </Box>
                    <VStack spacing={2}>
                      <Heading size="md" color={headingColor}>
                        {subdivisions.length === 0
                          ? 'No Sajja Sub-Divisions Found'
                          : 'No Matching Records'}
                      </Heading>
                      <Text color={subtitleColor}>
                        {subdivisions.length === 0
                          ? 'Start by adding your first sub-division'
                          : 'Try adjusting your search or filter criteria'}
                      </Text>
                    </VStack>
                  </VStack>
                </CardBody>
              </Card>
            ) : (
              <Box
                borderRadius="xl"
                overflow="hidden"
                border="1px solid"
                borderColor={borderColor}
                position="relative"
              >
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  height="4px"
                  bgGradient={`linear(to-r, ${gradientStart}, ${gradientEnd})`}
                />
                <Box
                  overflowY="auto"
                  maxH="500px"
                  css={{
                    '&::-webkit-scrollbar': { width: '8px' },
                    '&::-webkit-scrollbar-track': {
                      background: 'gray.100',
                      _dark: { background: 'gray.800' },
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: accentColor,
                      borderRadius: '4px',
                    },
                  }}
                >
                  <Table size="md" variant="simple">
                    <Thead
                      position="sticky"
                      top={0}
                      zIndex={2}
                      bg={tableHeaderBg}
                    >
                      <Tr>
                        <Th
                          textAlign="center"
                          color="white"
                          fontSize="sm"
                          py={4}
                        >
                          <Center>
                            <Icon as={MdAdminPanelSettings} mr={2} />#
                          </Center>
                        </Th>
                        <Th color="white" fontSize="sm" py={4}>
                          <Flex align="center" gap={2}>
                            <Icon as={GiCastle} />
                            Sajja
                          </Flex>
                        </Th>
                        <Th color="white" fontSize="sm" py={4}>
                          <Flex align="center" gap={2}>
                            <Icon as={GiVillage} />
                            Circle
                          </Flex>
                        </Th>
                        <Th color="white" fontSize="sm" py={4}>
                          <Flex align="center" gap={2}>
                            <Icon as={FaCity} />
                            Tahsil
                          </Flex>
                        </Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {filteredSubdivisions.map((sub, index) => (
                        <Tr
                          key={sub._id || index}
                          _hover={{ bg: rowHoverBg }}
                          cursor="pointer"
                        >
                          <Td textAlign="center" py={3}>
                            <Badge
                              colorScheme="blue"
                              variant="subtle"
                              borderRadius="full"
                              px={3}
                              py={1}
                            >
                              {index + 1}
                            </Badge>
                          </Td>
                          <Td py={3}>
                            <Flex align="center" gap={3}>
                              <Avatar
                                size="sm"
                                name={sub.name}
                                bg="blue.500"
                                color="white"
                              />
                              <Box>
                                <Text fontWeight="bold" color={textColor}>
                                  {sub.name?.toUpperCase() || 'N/A'}
                                </Text>
                              </Box>
                            </Flex>
                          </Td>
                          <Td py={3}>
                            <Flex align="center" gap={2}>
                              <Icon as={GiVillage} color="green.500" />
                              <Text fontSize="sm">
                                {sub.circle?.name?.toUpperCase() || '-'}
                              </Text>
                            </Flex>
                          </Td>
                          <Td py={3}>
                            <Flex align="center" gap={2}>
                              <Icon as={FaCity} color="blue.500" />
                              <Text fontSize="sm">
                                {sub.tahsil?.name?.toUpperCase() || '-'}
                              </Text>
                            </Flex>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </Box>
            )}
          </Box>

          <Flex
            justify="space-between"
            align="center"
            mt={6}
            pt={4}
            borderTop="1px solid"
            borderColor={borderColor}
            position="relative"
            zIndex={1}
          >
            <VStack align="flex-start" spacing={1}>
              <Text fontSize="sm" color={subtitleColor}>
                Showing <strong>{filteredSubdivisions.length}</strong> of{' '}
                <strong>{subdivisions.length}</strong> sub-divisions
              </Text>
              {filterTahsil && (
                <Text fontSize="xs" color="blue.500">
                  Filtered by:{' '}
                  <strong>
                    {tahsils.find((t) => t._id === filterTahsil)?.name ||
                      'Tahsil'}
                  </strong>
                </Text>
              )}
            </VStack>
            <HStack spacing={4}>
              <Text fontSize="xs" color={subtitleColor}>
                Updated:{' '}
                {new Date().toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </Text>
            </HStack>
          </Flex>
        </CardBody>
      </Card>
    </Box>
  );
};

export default React.memo(SajjaSection);
